import { type ReactNode } from "react";

/**
 * "How this was made" — implementation walk-through with architecture
 * diagrams. Diagrams are generated by scripts/build_architecture_diagrams.py
 * via the drawio CLI and bundled into the SPA at /docs/architecture/.
 */

const ARCH = "/docs/architecture";

interface SectionProps {
  number: string;
  title: string;
  image: string;
  alt: string;
  children: ReactNode;
}

function Section({ number, title, image, alt, children }: SectionProps) {
  return (
    <section className="space-y-5">
      <div>
        <div className="text-[14px] uppercase tracking-wider font-bold text-[var(--color-text-secondary)]">
          Phase {number}
        </div>
        <h2 className="gds-m mt-1 mb-0">{title}</h2>
      </div>
      <figure className="m-0">
        <img
          src={image}
          alt={alt}
          loading="lazy"
          className="w-full max-w-full h-auto bg-white"
        />
      </figure>
      <div className="space-y-4 text-[19px] leading-[1.47] text-[var(--color-text)]">
        {children}
      </div>
    </section>
  );
}

export function HowThisWasMadeView() {
  return (
    <div className="max-w-[1100px] mx-auto px-4 md:px-8 py-16 space-y-16">
      {/* ---- intro ---- */}
      <div>
        <h1 className="gds-l mb-4">How this was made</h1>
        <p className="text-[19px] leading-[1.47] text-[var(--color-text-secondary)] mb-0">
          The implementation behind the demo, in five phases. Every diagram
          on this page was generated from a small Python DSL via the{" "}
          <code>drawio</code> CLI, so they regenerate whenever the
          underlying architecture changes.
        </p>
      </div>

      {/* ---- system overview ---- */}
      <section className="space-y-5">
        <div>
          <div className="text-[14px] uppercase tracking-wider font-bold text-[var(--color-text-secondary)]">
            Overview
          </div>
          <h2 className="gds-m mt-1 mb-0">The whole thing on one page</h2>
        </div>
        <figure className="m-0">
          <img
            src={`${ARCH}/01-system-overview.png`}
            alt="High-level architecture: ingest pipeline at the top, browser-facing path through CloudFront and the API Gateway, NLQ Lambda chaining Bedrock + S3 Vectors + Athena + Iceberg"
            loading="lazy"
            className="w-full max-w-full h-auto bg-white"
          />
        </figure>
        <div className="space-y-4 text-[19px] leading-[1.47] text-[var(--color-text)]">
          <p>
            Three loosely-coupled subsystems sit in this one repo:
          </p>
          <ul className="list-disc pl-6 space-y-1">
            <li>
              an <strong className="font-bold">ingest pipeline</strong> that
              turns gzipped AWS Config snapshots into rows in an Iceberg
              table on S3, via SQS-batched Lambdas
            </li>
            <li>
              a <strong className="font-bold">retrieval-augmented
              generation</strong> layer that takes a natural-language
              question, finds the relevant resource type schemas in S3
              Vectors, and asks Claude to write a single Athena{" "}
              <code>SELECT</code> — wrapped in an async
              submit-and-poll job pattern so long queries aren&apos;t
              hostage to API Gateway&apos;s 30 second timeout
            </li>
            <li>
              a <strong className="font-bold">React SPA</strong> on
              CloudFront + S3 that talks to the same API Gateway HTTP API
              you&apos;re reading this through
            </li>
          </ul>
          <p>
            The rest of this page walks each phase end to end, in the order
            they were built.
          </p>
        </div>
      </section>

      <Section
        number="01"
        title="Ingest pipeline"
        image={`${ARCH}/02-ingest-pipeline.png`}
        alt="Ingest pipeline: mock generator uploads gzipped JSON to S3, S3 events fire into an SQS queue, the extract Lambda batches 25 files per invocation and INSERTs into the Iceberg table via Athena, with a nightly compact Lambda running MERGE/OPTIMIZE/VACUUM"
      >
        <p>
          Phase 1 ships gzipped AWS Config snapshots from a mock S3 bucket
          into an Iceberg table that everything downstream queries. The
          choice that matters here is <strong className="font-bold">SQS
          batching with a low concurrency cap</strong>: every snapshot
          uploaded fires an S3 event that lands on{" "}
          <code>cinq-extract-queue</code>, the queue&apos;s event source
          mapping batches up to 25 messages, and the Lambda runs at a hard
          reserved concurrency of 5. Without that cap, 500 simultaneous
          Iceberg commits cause optimistic-concurrency commit storms
          against the Glue catalog and the whole pipeline grinds.
        </p>
        <p>
          The hot path uses{" "}
          <code>awswrangler.athena.to_iceberg</code> in append mode —
          server-side INSERT INTO, not direct Iceberg writes from the
          Lambda. Every night the compact Lambda runs a single Athena
          script that does <code>MERGE INTO</code>, deletes anything older
          than the TTL, optimises file layout and vacuums old metadata.
          That keeps the table&apos;s small-file count under control and
          gives the &quot;deleted in account → disappears from queries&quot; semantic
          for free.
        </p>
      </Section>

      <Section
        number="02"
        title="Schema RAG indexing"
        image={`${ARCH}/03-rag-indexing.png`}
        alt="Schema indexing pipeline: 417 awslabs schema files are run through Claude Sonnet 4.6 for enrichment, the resulting markdown docs are embedded with Titan Text Embeddings v2, and the vectors are upserted into an S3 Vectors index"
      >
        <p>
          The natural-language layer can&apos;t fit all 417 AWS Config
          resource type schemas into a Bedrock prompt simultaneously, so
          phase 2 builds a retrieval index. Each raw{" "}
          <code>.properties.json</code> from{" "}
          <code>awslabs/aws-config-resource-schema</code> is enriched into
          a semantic markdown doc by Claude — a short description, a
          category, the most useful fields, example questions, related
          resource types — and that whole doc is embedded by Titan Text
          Embeddings v2 into a 1024-dimension vector.
        </p>
        <p>
          The vectors live in{" "}
          <strong className="font-bold">AWS S3 Vectors</strong> rather than
          OpenSearch or Pinecone. The data fits in a single index, the
          query rate is low, and S3 Vectors&apos; pricing model
          (per-vector storage + per-query) is two orders of magnitude
          cheaper than provisioning a vector database for this workload.
          The whole indexing run is a one-off — about $10 of Claude
          tokens, run idempotently, and re-run only when awslabs publishes
          new resource types.
        </p>
      </Section>

      <Section
        number="03"
        title="Async NLQ HTTP API"
        image={`${ARCH}/04-nlq-runtime.png`}
        alt="Async NLQ runtime path: POST /nlq hits API Gateway, an authoriser Lambda checks x-api-key against Secrets Manager, a submit Lambda writes an initial progress doc to an S3 jobs bucket and async-invokes a worker Lambda before returning 202 Accepted; the worker runs Titan embed, S3 Vectors retrieve, Claude generate, and Athena query in sequence while overwriting the jobs bucket after every stage; the browser polls GET /nlq/jobs/{id} which reads the same doc"
      >
        <p>
          The runtime path is where the most interesting architectural
          decision lives. The original version held one HTTP connection
          open for the whole request — browser waits, Lambda runs embed
          → retrieve → Claude → Athena, Lambda returns the rows, socket
          closes. That worked up to about 15 seconds end to end, but
          API&nbsp;Gateway&nbsp;v2&apos;s HTTP API has a <em>hard</em> 30
          second integration timeout with no override, and as the data
          grew some queries — 4-way VPC pivots, orphan-KMS anti-joins —
          started spending 8 seconds in Claude <em>plus</em> 8 seconds
          in Athena and shipping occasional 504s. A 504 on a perfectly
          valid query is the worst possible demo failure: the user
          thinks the engine is broken when it&apos;s actually fine.
        </p>
        <p>
          The fix is the standard REST async-job pattern. <code>POST /nlq</code>
          does as little as possible: the authoriser checks the key,
          the submit Lambda validates the body, allocates a{" "}
          <code>job_id</code>, writes an initial progress JSON to a new
          jobs bucket, <code>lambda.invoke</code>s the worker with{" "}
          <code>InvocationType=Event</code>, and returns <strong className="font-bold">
          202 Accepted</strong> with <code>{'{job_id, status_url}'}</code>{" "}
          in under half a second. No socket held open, no 30-second
          ceiling in sight.
        </p>
        <p>
          The worker Lambda then runs the four stages it used to run
          synchronously — Titan embed, <code>s3vectors.query_vectors</code>,
          Claude Sonnet 4.6 via the global cross-region inference profile,
          and <code>athena.start_query_execution</code> against the
          Iceberg table from phase&nbsp;1 — except now with a 5-minute
          timeout instead of 29 seconds, and <strong className="font-bold">
          writing the jobs-bucket doc after every stage transition</strong>.
          The doc carries per-stage <code>status</code>, server-stamped{" "}
          <code>started_at</code> timestamps, real <code>ms</code> on
          completion, and ultimately the full <code>result</code> block
          (SQL, retrieved schemas, rows, Athena statistics, timings) or
          an <code>error</code> block.
        </p>
        <p>
          The browser polls <code>GET /nlq/jobs/{'{id}'}</code> every 800 ms.
          That route goes through the same authoriser + submit Lambda;
          the Lambda branches on method, reads the S3 doc, and returns
          it verbatim. Polling stops when the status is terminal. The
          sticky progress bar and the in-panel progress card are driven
          by the same shared derivation that prefers real per-stage data
          from the poll and fills the sub-poll gaps with a synthetic
          timeline — so the first stage animates the moment the
          202&nbsp;lands, sub-second stages still visibly move, and
          terminal polls snap every bar to its real number.
        </p>
      </Section>

      <Section
        number="04"
        title="SPA hosting"
        image={`${ARCH}/05-spa-hosting.png`}
        alt="SPA hosting: Route 53 alias points at a CloudFront distribution that fronts a private S3 bucket via Origin Access Control, with an ACM certificate in us-east-1; the Vite build is synced and CloudFront invalidated by a single make target"
      >
        <p>
          The SPA you&apos;re reading this in is a flat React + Vite
          bundle on a private S3 bucket, fronted by CloudFront with an
          Origin Access Control. The TLS certificate has to live in{" "}
          <code>us-east-1</code> regardless of where the rest of the stack
          runs, so the Terraform uses a second AWS provider alias just for
          the cert. Route 53 ties{" "}
          <code>nlq.demos.apps.equal.expert</code> to the CloudFront
          distribution via an alias record.
        </p>
        <p>
          The deploy is a single <code>make spa-deploy</code> — Vite
          builds with the API base URL injected at build time, a sync
          uploads everything except <code>index.html</code> with
          immutable cache headers, <code>index.html</code> goes up with
          no-cache, and the whole prefix is invalidated on CloudFront in
          one call.
        </p>
      </Section>

      {/* ---- footer pointer ---- */}
      <div className="border-t border-[var(--color-border)] pt-8">
        <p className="text-[16px] text-[var(--color-text-secondary)] mb-0">
          The full per-phase breakdown — every file changed, every
          gotcha encountered, every cost number — lives in{" "}
          <a
            href="https://github.com/reaandrew/cloud-infra-nlq-query/tree/main/docs"
            target="_blank"
            rel="noreferrer"
          >
            docs/
          </a>{" "}
          on GitHub, in five files: phase-1 through phase-5.
        </p>
      </div>
    </div>
  );
}
